import { useEffect, useMemo, useState } from 'react';
import { api, errMsg } from '../../lib/api';
import { PHRASE_SCENES } from '../../components/PhrasePicker';

interface Phrase {
  id: string;
  content: string;
  scene: string;
  useCount: number;
  sortOrder: number;
  isEnabled: boolean;
}

/** 后台：维护祭奠页的追思常用语（场景分类 + 使用次数统计） */
export default function AdminPhrases() {
  const [items, setItems] = useState<Phrase[]>([]);
  const [draft, setDraft] = useState('');
  const [editScene, setEditScene] = useState('通用');
  const [editing, setEditing] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'order' | 'count'>('order');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const res = await api<{ items: Phrase[] }>('/admin/phrases');
    if (res.ok && res.data) setItems(res.data.items);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const shown = useMemo(() => {
    const arr = [...items];
    if (sortBy === 'count') arr.sort((a, b) => b.useCount - a.useCount);
    else arr.sort((a, b) => a.sortOrder - b.sortOrder);
    return arr;
  }, [items, sortBy]);

  const totalUse = useMemo(() => items.reduce((s, p) => s + p.useCount, 0), [items]);

  const sceneOptions = useMemo(() => {
    const set = new Set<string>(PHRASE_SCENES);
    items.forEach((p) => set.add(p.scene));
    return Array.from(set);
  }, [items]);

  async function save() {
    if (!draft.trim()) {
      setNotice('内容不能为空');
      return;
    }
    const content = draft.trim();
    const res = editing
      ? await api(`/admin/phrases/${editing}`, {
          method: 'PATCH',
          body: JSON.stringify({ content, scene: editScene }),
        })
      : await api('/admin/phrases', {
          method: 'POST',
          body: JSON.stringify({
            content,
            scene: editScene,
            sortOrder: (items[items.length - 1]?.sortOrder ?? 0) + 10,
          }),
        });

    if (!res.ok) {
      setNotice(`${editing ? '保存' : '新增'}失败：${errMsg(res, '未知错误')}`);
      return;
    }
    setNotice(editing ? '已保存' : '已新增');
    setDraft('');
    setEditScene('通用');
    setEditing(null);
    load();
  }

  async function toggle(p: Phrase) {
    await api(`/admin/phrases/${p.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isEnabled: !p.isEnabled }),
    });
    load();
  }

  async function setOrder(p: Phrase, v: number) {
    await api(`/admin/phrases/${p.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ sortOrder: v }),
    });
    load();
  }

  async function remove(p: Phrase) {
    if (!window.confirm(`确定删除这条常用语？\n${p.content}`)) return;
    const res = await api(`/admin/phrases/${p.id}`, { method: 'DELETE' });
    if (!res.ok) {
      setNotice(`删除失败：${errMsg(res, '未知错误')}`);
      return;
    }
    setNotice('已删除');
    load();
  }

  if (loading) return <p className="text-sm text-ink-500">加载中…</p>;

  return (
    <div>
      {notice && (
        <p className="mb-4 rounded-lg bg-candle-300/20 px-4 py-2.5 text-sm text-ink-700">
          {notice}
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium text-ink-900">
          追思常用语（{items.length}）· 累计选用 {totalUse} 次
        </h2>
        <div className="flex gap-1.5">
          <button
            onClick={() => setSortBy('order')}
            className={`rounded-full px-2.5 py-1 text-[10px] ${
              sortBy === 'order' ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-700'
            }`}
          >
            按排序
          </button>
          <button
            onClick={() => setSortBy('count')}
            className={`rounded-full px-2.5 py-1 text-[10px] ${
              sortBy === 'count' ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-700'
            }`}
          >
            按热度
          </button>
        </div>
      </div>

      <div className="mb-5 rounded-xl border border-ink-200 bg-ink-50/60 p-4">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="输入常用语内容…"
          className="w-full rounded-md border border-ink-200 bg-white px-3 py-2 text-sm outline-none"
        />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            value={editScene}
            onChange={(e) => setEditScene(e.target.value)}
            className="h-8 rounded-md border border-ink-200 bg-white px-2 text-xs outline-none"
          >
            {sceneOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            onClick={save}
            className="rounded-full bg-ink-900 px-4 py-1.5 text-xs text-white"
          >
            {editing ? '保存修改' : '+ 新增常用语'}
          </button>
          {editing && (
            <button
              onClick={() => {
                setEditing(null);
                setDraft('');
                setEditScene('通用');
              }}
              className="rounded-full px-3 py-1.5 text-xs text-ink-500"
            >
              取消编辑
            </button>
          )}
        </div>
      </div>

      <ul className="space-y-2">
        {shown.map((p) => (
          <li
            key={p.id}
            className="flex items-start gap-2 rounded-lg border border-ink-200 bg-white p-3"
          >
            <input
              type="number"
              defaultValue={p.sortOrder}
              onBlur={(e) => setOrder(p, Number(e.target.value))}
              className="h-8 w-14 shrink-0 rounded border border-ink-200 px-1 text-[11px] outline-none"
            />
            <div className="min-w-0 flex-1">
              <p
                className={`text-sm leading-relaxed ${
                  p.isEnabled ? 'text-ink-900' : 'text-ink-400 line-through'
                }`}
              >
                {p.content}
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px]">
                <span className="rounded-full bg-ink-100 px-2 py-0.5 text-ink-700">
                  {p.scene}
                </span>
                <span className="text-ink-500">选用 {p.useCount} 次</span>
              </p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <button
                onClick={() => toggle(p)}
                className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] text-emerald-700"
              >
                {p.isEnabled ? '已启用' : '已停用'}
              </button>
              <button
                onClick={() => {
                  setEditing(p.id);
                  setDraft(p.content);
                  setEditScene(p.scene);
                }}
                className="rounded-full bg-ink-100 px-2.5 py-1 text-[10px] text-ink-700"
              >
                编辑
              </button>
              <button
                onClick={() => remove(p)}
                className="rounded-full bg-red-50 px-2.5 py-1 text-[10px] text-red-600"
              >
                删除
              </button>
            </div>
          </li>
        ))}
      </ul>

      <p className="mt-6 text-[11px] leading-relaxed text-ink-400">
        提示：停用的常用语不会出现在祭奠页；排序数字越小越靠前；「按热度」可按被选用次数排序，便于把高频文案往前放。
      </p>
    </div>
  );
}
